import React, { Dispatch, SetStateAction, useRef, useState } from 'react'
import { Container, ContainerTable, TableFiltersHeader, Strong } from './styles'
import { HealthProcedureDetails } from '~/components/healthProcedures/healthProcedureDetails'
import { Input, Select } from '~/components/shared'
import { Table } from '~/components/shared/Table'
import { useLaboratoryHealthProcedures } from '~/context'
import { message } from '~/services'
import { transformDate } from '~/utils'
import { IUrineCultureTest } from '~/interfaces'

interface Props {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}
interface IdataTable {
  date: string
  result: string | React.ReactElement
  organism: string[]
}

const CompareUrineCulture = ({ showModal, setShowModal }: Props): JSX.Element => {
  const { urineCultureTests } = useLaboratoryHealthProcedures()

  const inputFilter = useRef<HTMLInputElement>(null)
  const [selectedGrowth, setSelectedGrowth] = useState<'positivo' | 'negativo' | 'todos'>('todos')
  const [allowRefresh, setAllowRefresh] = useState(false)
  const [descriptionFilter, setDescriptionFilter] = useState('')

  const filterByGrowth = (urineCultures: IUrineCultureTest[]) => {
    const isFilterEmpty = selectedGrowth === 'todos'
    if (isFilterEmpty) return urineCultures

    return urineCultures.filter(urineCulture => urineCulture.organismGrowth?.toLowerCase() === selectedGrowth)
  }

  const filterByOrganism = (urineCultures: IUrineCultureTest[]) => {
    const isFilterEmpty = descriptionFilter === ''
    if (isFilterEmpty) return urineCultures

    return urineCultures.filter(urineCulture => {
      const organisms = urineCulture.organism
      return organisms?.some(organism => organism.toLowerCase()?.includes(descriptionFilter.toLowerCase()))
    })
  }

  const parseUrineCultureToRowData = (urineCultures: IUrineCultureTest[]): IdataTable[] => {
    const filteredUrineCultures = filterByOrganism(filterByGrowth(urineCultures))

    const groupedNamesById = filteredUrineCultures.reduce((acc, urineCulture) => {
      const { organism, organismGrowth, startDate, id } = urineCulture
      const organisms = organism?.join(', ')

      if (acc[id]) {
        return {
          ...acc,
          [id]: { ...acc[id], organism: [acc[id].organism, organisms].join(', ') }
        }
      }

      return {
        ...acc,
        [id]: {
          date: transformDate({ date: startDate }),
          result:
            organismGrowth?.toLowerCase() === 'positivo' ? (
              <Strong>{organismGrowth.toLowerCase()}</Strong>
            ) : (
              organismGrowth
            ),
          organism: organisms
        }
      }
    }, {})

    return Object.values(groupedNamesById)
  }

  const handleSearch = text => {
    if (text.target.value !== '') {
      setAllowRefresh(true)
    }
    setDescriptionFilter(text.target.value)
  }

  const setSelectedItem = value => {
    if (value !== 'Todos') {
      setAllowRefresh(true)
    }
    setSelectedGrowth(value)
  }

  const handleRefresh = () => {
    setDescriptionFilter('')
    inputFilter.current!.value = ''
    setAllowRefresh(false)
  }

  return (
    <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="BigModal">
      <Container>
        <h1>Comparativo de Uroculturas</h1>
        <ContainerTable>
          <TableFiltersHeader>
            <Select
              name="select"
              value={selectedGrowth}
              setValue={setSelectedItem}
              options={[
                {
                  label: 'Todos',
                  value: 'todos'
                },
                {
                  label: 'Positivo',
                  value: 'positivo'
                },
                {
                  label: 'Negativo',
                  value: 'negativo'
                }
              ]}
            />
            <Input
              icon="search"
              ref={inputFilter}
              name="search_diagnosis"
              onChange={handleSearch}
              placeholder="Buscar"
            />
          </TableFiltersHeader>

          <Table
            data={parseUrineCultureToRowData(urineCultureTests)}
            messageError={message('laboratory.search_not_found')}
            headerLineHeight="30px"
            headerPadding="10px 0 10px 24px"
            minHeight="300px"
            onRefresh={allowRefresh ? handleRefresh : undefined}
            model={[
              {
                key: 'date',
                label: message('laboratory.date').toUpperCase(),
                width: '60px',
                sortable: true
              },
              {
                key: 'result',
                label: message('laboratory.result').toUpperCase(),
                sortable: true,
                width: '140px'
              },
              {
                key: 'organism',
                label: message('laboratory.organism').toUpperCase(),
                sortable: true,
                width: '150px'
              }
            ]}
          />
        </ContainerTable>
      </Container>
    </HealthProcedureDetails>
  )
}

export { CompareUrineCulture }
